import axios from "axios";
import { Network } from "@capacitor/network";
import { db } from "./db";
import module_parser from "./module_parser";
import patch_post_parser from "./patch_post_parser";
import uuid from "@/plugins/uuid";
import localDelete from "./local_delete";
import store from "@/store";

const getApiUrl = () => {
    return store.getters.getApiUrl;
};

const requests = {
    get: async (module, url, params = null) => {
        // get from server if online
        if (store.getters.online && getApiUrl()) {
            await axios
                .get(getApiUrl() + url)
                .then(async (response) => {
                    let records = response.data;

                    // parse the response
                    let parsed_records = await module_parser(module, records);

                    // Get all the items from the local database
                    let local_items = await db[module].toArray().then((items) => {
                        return items.filter((item) => !item.not_synced);
                    });

                    // Create a Set of response item IDs or UUIDs
                    let response_item_ids = new Set(parsed_records.map((record) => record.uuid || record.id));

                    // Delete local items that are not in the response
                    for (const item of local_items) {
                        let item_key = item.uuid || item.id;

                        if (!response_item_ids.has(item_key)) {
                            if (params) {
                                // delete only children of the parent that match params
                                if (Object.keys(params).every((key) => item[key] === params[key])) {
                                    if (item.uuid) {
                                        await db[module].where({ uuid: item.uuid }).delete();
                                    } else {
                                        await db[module].where({ id: item.id }).delete();
                                    }
                                }
                            } else {
                                // delete the item
                                if (item.uuid) {
                                    await db[module].where({ uuid: item.uuid }).delete();
                                } else {
                                    await db[module].where({ id: item.id }).delete();
                                }
                            }
                        }
                    }

                    console.log("Synced " + module + " from server");

                    return await db[module].bulkPut(parsed_records);
                })
                .catch((error) => {
                    console.log(error);
                });
        } else {
            console.log("Offline mode, not syncing " + module);
        }

        // get from local storage
        let result = null;
        if (!params) {
            result = await db[module].toArray();
        } else {
            result = await db[module].where(params).toArray();
        }

        return result;
    },
    patch: async (module, url, payload) => {
        // Validate URL is not empty or undefined
        if (!url || url.trim() === '') {
            console.error(`[requests.patch] Empty or invalid URL provided for module: ${module}`);
            return payload; // Return payload without queuing job
        }
        
        // Validate payload is not empty or undefined
        if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
            console.error(`[requests.patch] Empty or invalid payload provided for module: ${module}`);
            return payload; // Return payload without queuing job
        }

        let cloned_payload = JSON.parse(JSON.stringify(payload));
        let parsed_url = url.replaceAll("_", "-");

        // parse it
        cloned_payload = patch_post_parser(module, cloned_payload);

        await db[module].put(cloned_payload);

        let result = null;

        if (module == "notifications") {
            result = await db[module].where({ id: cloned_payload.id }).first();
        } else {
            result = await db[module].where({ uuid: cloned_payload.uuid }).first();
        }

        //push to queue
        // uuid is required: SyncManager.toFailedJobs dedupes failed jobs on it,
        // and a job without uuid could never be moved to failed_jobs (it would
        // retry forever without ever incrementing its attempt count).
        await db.jobs.add({
            uuid: uuid(),
            module: module,
            url: parsed_url,
            payload: cloned_payload,
            type: "patch",
        });

        return result;
    },
    patchArray: async (module, url, payload) => {
        // Validate URL is not empty or undefined
        if (!url || url.trim() === '') {
            console.error(`[requests.patchArray] Empty or invalid URL provided for module: ${module}`);
            return payload; // Return payload without queuing job
        }

        // Validate payload is not empty or undefined
        if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
            console.error(`[requests.patchArray] Empty or invalid payload provided for module: ${module}`);
            return payload; // Return payload without queuing job
        }

        let cloned_payload = JSON.parse(JSON.stringify(payload));
        let parsed_url = url.replaceAll("_", "-");

        let first_property = Object.keys(cloned_payload)[0];

        // parse it
        cloned_payload[module].forEach((item) => {
            let parsed_item = patch_post_parser(module, item);
            item = parsed_item;
        });

        // Persist the full objects locally so UI queries against Dexie keep
        // every field. The backend queue payload below is trimmed separately.
        let result = await db[module].bulkPut(cloned_payload[first_property]);

        // Build a minimal payload for the backend queue to reduce bandwidth.
        // The full objects are already persisted to Dexie above — trimming
        // here only affects what travels over the network.
        let backend_payload = JSON.parse(JSON.stringify(cloned_payload));

        if (module === 'location_items') {
            backend_payload[module] = backend_payload[module].map(item => ({
                uuid: item.uuid,
                name: item.name,
                order_id: item.order_id,
                parent_uuid: item.parent_uuid ?? null,
                updated_at: item.updated_at,
            }));
        } else if (module === 'location_item_components') {
            backend_payload[module] = backend_payload[module].map(item => ({
                uuid: item.uuid,
                order_id: item.order_id,
                parent_uuid: item.parent_uuid ?? null,
                location_item_uuid: item.location_item_uuid,
                inspection_id: item.inspection_id,
                updated_at: item.updated_at,
            }));
        }

        //push to queue with minimal payload
        await db.jobs.put({
            uuid: uuid(),
            module: module,
            url: parsed_url,
            payload: backend_payload,
            type: "patchArray",
        });

        return result;
    },
    // record_uuid: identifier of the record to delete (was named `uuid`, renamed so
    // the imported uuid() generator isn't shadowed when creating the job below)
    delete: async (module, url, record_uuid) => {
        // delete locally
        let params = { uuid: record_uuid };

        if (module == "notifications" || module == "remarks" || module == "measurement_instruments") {
            params = { id: record_uuid };
        }

        //push to queue
        if (url) {
            let shouldCreateJob = true;

            if (module === "images") {
                // Check image sync status BEFORE deleting it locally
                const image = await db.images.get(record_uuid);

                // Check for pending upload jobs for this image
                const pendingUploadJob = await db.jobs
                    .filter(job =>
                        (job.type === 'image-s3-upload' && job.payload?.image_uuid === record_uuid) ||
                        (job.type === 'image-s3-update' && job.payload?.existing_image_uuid === record_uuid)
                    )
                    .first();

                const failedUploadJob = await db.failed_jobs
                    .filter(job =>
                        (job.type === 'image-s3-upload' && job.payload?.image_uuid === record_uuid) ||
                        (job.type === 'image-s3-update' && job.payload?.existing_image_uuid === record_uuid)
                    )
                    .first();

                if (pendingUploadJob) {
                    // Image has pending upload job - cancel it instead of creating delete job
                    console.log(`Canceling pending upload job ${pendingUploadJob.id} for image ${record_uuid}`);
                    await db.jobs.delete(pendingUploadJob.id);
                    shouldCreateJob = false;
                } else if (failedUploadJob) {
                    // Image has failed upload job - remove it to prevent retry
                    console.log(`Removing failed upload job ${failedUploadJob.id} for image ${record_uuid}`);
                    await db.failed_jobs.delete(failedUploadJob.id);
                    shouldCreateJob = false;
                } else {
                    // No pending upload - check if image was synced
                    // Only create delete job if image exists on backend
                    // For backward compatibility: if sync_status is not set but s3_key exists,
                    // assume it was synced (images downloaded/uploaded before sync_status was added)
                    shouldCreateJob = image && (
                        image.sync_status === 'synced' ||
                        (!image.sync_status && image.s3_key)
                    );

                    if (!shouldCreateJob) {
                        console.log(`Skipping delete job for image ${record_uuid} - not synced to backend`);
                    }
                }
            }

            // cascade delete (must happen after checking sync status)
            await localDelete(module, params);

            if (shouldCreateJob) {
                // uuid required for the failed-job dedup in SyncManager.toFailedJobs
                await db.jobs.add({
                    uuid: uuid(),
                    module: module,
                    url: url,
                    type: "delete",
                });
            }
        } else {
            // No URL provided, just delete locally
            await localDelete(module, params);
        }
    },
    post: async (module, url, payload, table_name) => {
        // Validate URL is not empty or undefined
        if (!url || url.trim() === '') {
            console.error(`[requests.post] Empty or invalid URL provided for module: ${module}`);
            return payload; // Return payload without queuing job
        }
        
        // Validate payload is not empty or undefined
        if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
            console.error(`[requests.post] Empty or invalid payload provided for module: ${module}`);
            return payload; // Return payload without queuing job
        }

        let cloned_payload = JSON.parse(JSON.stringify(payload));
        let parsed_url = url.replaceAll("_", "-");

        const subject_name = table_name ? table_name : module;

        // if module is components, we use base_components as table name for local db
        if (module == "components") {
            module = "base_components";
        }

        if (Array.isArray(cloned_payload[subject_name])) {
            cloned_payload[subject_name] = cloned_payload[subject_name].map((item) => {
                return patch_post_parser(module, item, "post");
            });

            await db[module].bulkPut(cloned_payload[subject_name]);
        } else {
            cloned_payload = patch_post_parser(module, cloned_payload, "post");

            await db[module].put(cloned_payload).catch((error) => {
                console.log(error);
            });
        }

        // Check for duplicate jobs with the same URL and payload UUIDs to prevent double submissions
        try {
            const existingJobs = await db.jobs.where('url').equals(parsed_url).toArray();
            const payloadUuids = Array.isArray(cloned_payload[subject_name])
                ? cloned_payload[subject_name].map(item => item.uuid).filter(Boolean)
                : [cloned_payload.uuid].filter(Boolean);

            const isDuplicate = payloadUuids.length > 0 && existingJobs.some(existingJob => {
                const existingPayload = existingJob.payload;
                if (!existingPayload) return false;
                const existingUuids = Array.isArray(existingPayload[subject_name])
                    ? existingPayload[subject_name].map(item => item.uuid).filter(Boolean)
                    : [existingPayload.uuid].filter(Boolean);
                return payloadUuids.some(u => existingUuids.includes(u));
            });

            if (isDuplicate) {
                console.warn(`[requests.post] Duplicate job detected for ${module} with matching UUIDs, skipping queue`);
                return payload;
            }
        } catch (e) {
            console.warn('[requests.post] Duplicate check failed, skipping:', e);
        }

        //push to queue
        await db.jobs.put({
            uuid: uuid(),
            module: module,
            url: parsed_url,
            payload: cloned_payload,
            type: "post",
        });

        return payload;
    },
    postDocument: async (module, url, payload, file) => {
        console.warn('DEPRECATED: postDocument using direct upload. Use documentUploadService instead for S3 presigned URL uploads.');

        let cloned_payload = JSON.parse(JSON.stringify(payload));
        let parsed_url = url.replaceAll("_", "-");

        // parse it
        cloned_payload = patch_post_parser(module, cloned_payload, "post");

        // we add it to local db
        await db[module].put(cloned_payload);

        //push to queue
        await db.jobs.put({
            uuid: uuid(),
            module: "documents",
            url: parsed_url,
            payload: cloned_payload,
            file: file,
            type: "upload-document",
        });

        return payload;
    },
    add: async (module, payload, params) => {
        if (!payload) {
            return;
        }

        let cloned_payload = JSON.parse(JSON.stringify(payload));

        if (store.getters.online) {
            let uuids = cloned_payload.map((item) => item.uuid);

            // delete local items so we only have the new ones
            if (params) {
                // delete only children of the parent
                await db[module]
                    .where(params)
                    .toArray()
                    .then(async (items) => {
                        let local_uuids = items.map((item) => item.uuid);
                        let uuids_to_delete = local_uuids.filter((uuid) => !uuids.includes(uuid));
                        await db[module].where("uuid").anyOf(uuids_to_delete).delete();
                    });
            } else {
                // delete all items
                await db[module].where("uuid").noneOf(uuids).delete();
            }
        }

        // parse module
        let parsed_payload = await module_parser(module, cloned_payload);

        await db[module].bulkPut(parsed_payload);
    },
    runFailedJobs: async () => {
        let failed_jobs = await db.failed_jobs.toArray();
        if (failed_jobs.length > 0) {
            for (let failed_job of failed_jobs) {
                if (failed_job.attempt < 3) {
                    failed_job.attempt = failed_job.attempt + 1;
                    await db.jobs.put(failed_job);
                    await db.failed_jobs.delete(failed_job.id);
                } else {
                    // push to permanently_failed_jobs and delete from failed_jobs
                    await db.permanently_failed_jobs.put(failed_job);
                    await db.failed_jobs.delete(failed_job.id);
                }
            }
        }
        requests.runQueue();
    },
    toFailedJobs: async (job, error) => {
        let cloned_job = JSON.parse(JSON.stringify(job));
        cloned_job.error_message = error.message;
        cloned_job.attempt = cloned_job.attempt ? cloned_job.attempt + 1 : 0;
        if (error && error.request && error.request.status) {
            cloned_job.error_code = error.request.status;
        }

        if (cloned_job.error_code != 404) {
            db.failed_jobs.put(cloned_job).then(() => {
                console.log("Job added to failed jobs");
                requests.deleteJob(job);
            });
        } else {
            requests.deleteJob(job);
        }
    },
    deleteJob: async (job) => {
        console.log("Deleting job " + job.id);
        await db.jobs.delete(job.id);
    },
    postPermanentFailedJob: async (job) => {getApiUrl()
        if (store.getters.getServerStatus == "offline") {
            return;
        }

        await axios
            .post(getApiUrl() + "/failed-jobs", {
                data: job,
            })
            .then(async (response) => {
                console.log("Failed job response:", response.data);

                // delete the job after successful processing from permanently_failed_jobs
                await db.permanently_failed_jobs.delete(job.id);
            })
            .catch((error) => {
                console.log("Permanently failed job error:", error);
            });
    },
};

export default requests;
